import api from './api';
import {
    getBrowserInstallDeviceId,
    isInstalledDisplayMode
} from './appInstallation';

const ONBOARDING_STORAGE_PREFIX = 'argus.notificationPermissionOnboarding.v3';
const NOTIFICATION_ONBOARDING_SNOOZE_MS = 3 * 24 * 60 * 60 * 1000;
const WEB_PUSH_CONFIG_URL = '/api/web-push/config';
const WEB_PUSH_SUBSCRIPTIONS_URL = '/api/web-push/subscriptions';

let registrationPromise = null;
let preparedRegistration = null;
let preparedSubscription = null;
let preparedConfig = null;

export function isInstalledWebApp() {
    return isInstalledDisplayMode();
}

export function isIOSDevice() {
    const userAgent = navigator.userAgent || '';
    return (
        /iPad|iPhone|iPod/i.test(userAgent)
        || (
            navigator.platform === 'MacIntel'
            && navigator.maxTouchPoints > 1
        )
    );
}

export function isAndroidDevice() {
    return /Android/i.test(navigator.userAgent || '');
}

export function supportsWebPush() {
    return (
        window.isSecureContext === true
        && 'Notification' in window
        && typeof Notification.requestPermission === 'function'
        && 'serviceWorker' in navigator
        && 'PushManager' in window
    );
}

function getNotificationOnboardingStorageKey(context = {}) {
    const identity = String(context.identity || '').trim();
    if (!identity) {
        return null;
    }
    const deviceId = String(
        context.deviceId || getBrowserInstallDeviceId()
    ).trim();
    return [
        ONBOARDING_STORAGE_PREFIX,
        deviceId || 'unknown-device',
        identity
    ].join(':');
}

function readNotificationOnboardingState(context = {}) {
    const storageKey = getNotificationOnboardingStorageKey(context);
    if (!storageKey) {
        return null;
    }
    try {
        const value = localStorage.getItem(storageKey);
        return value ? JSON.parse(value) : null;
    } catch (error) {
        return null;
    }
}

function writeNotificationOnboardingState(context, state) {
    const storageKey = getNotificationOnboardingStorageKey(context);
    if (!storageKey) {
        return;
    }
    try {
        localStorage.setItem(storageKey, JSON.stringify(state));
    } catch (error) {
        // The browser permission remains authoritative if localStorage is unavailable.
    }
}

export function isNotificationOnboardingSuppressed(context = {}) {
    const state = readNotificationOnboardingState(context);
    return Boolean(
        state
        && (
            state.status === 'disabled'
            || (
                state.status === 'snoozed'
                && Number(state.snoozedUntil) > Date.now()
            )
        )
    );
}

export function disableNotificationOnboarding(context = {}) {
    writeNotificationOnboardingState(context, {
        status: 'disabled',
        disabledAt: Date.now()
    });
}

export function clearNotificationOnboardingState(context = {}) {
    const storageKey = getNotificationOnboardingStorageKey(context);
    if (!storageKey) {
        return;
    }
    try {
        localStorage.removeItem(storageKey);
    } catch (error) {
        // The browser permission remains authoritative if localStorage is unavailable.
    }
}

export function snoozeNotificationOnboarding(context = {}) {
    writeNotificationOnboardingState(context, {
        status: 'snoozed',
        snoozedAt: Date.now(),
        snoozedUntil: Date.now() + NOTIFICATION_ONBOARDING_SNOOZE_MS
    });
}

export function getDisplayMode() {
    if (
        window.matchMedia
        && window.matchMedia('(display-mode: fullscreen)').matches
    ) {
        return 'fullscreen';
    }
    if (
        window.matchMedia
        && window.matchMedia('(display-mode: standalone)').matches
    ) {
        return 'standalone';
    }
    if (
        window.matchMedia
        && window.matchMedia('(display-mode: minimal-ui)').matches
    ) {
        return 'minimal-ui';
    }
    if (navigator.standalone === true) {
        return 'standalone';
    }
    return 'browser';
}

export function registerNotificationServiceWorker() {
    if (!('serviceWorker' in navigator)) {
        return Promise.resolve(null);
    }
    if (!registrationPromise) {
        registrationPromise = navigator.serviceWorker
            .register('/service-worker.js')
            .then(registration => {
                preparedRegistration = registration;
                return registration;
            })
            .catch(error => {
                registrationPromise = null;
                console.warn('Не удалось зарегистрировать service worker уведомлений', error);
                return null;
            });
    }
    return registrationPromise;
}

function base64UrlToUint8Array(value) {
    const padding = '='.repeat((4 - value.length % 4) % 4);
    const base64 = (value + padding)
        .replace(/-/g, '+')
        .replace(/_/g, '/');
    const raw = window.atob(base64);
    const output = new Uint8Array(raw.length);
    for (let index = 0; index < raw.length; index += 1) {
        output[index] = raw.charCodeAt(index);
    }
    return output;
}

function serializeSubscription(subscription) {
    const serialized = subscription && subscription.toJSON
        ? subscription.toJSON()
        : null;
    if (!serialized || !serialized.endpoint || !serialized.keys) {
        throw new Error('Браузер вернул неполную Web Push-подписку');
    }
    return serialized;
}

async function saveSubscription(subscription) {
    await api.put(WEB_PUSH_SUBSCRIPTIONS_URL, {
        displayMode: getDisplayMode(),
        subscription: serializeSubscription(subscription)
    });
}

export async function loadWebPushConfig(options = {}) {
    if (
        preparedConfig
        && preparedConfig.enabled
        && preparedConfig.applicationServerKey
        && options.force !== true
    ) {
        return preparedConfig;
    }
    const {data} = await api.get(WEB_PUSH_CONFIG_URL);
    preparedConfig = data || {
        enabled: false,
        applicationServerKey: null
    };
    return preparedConfig;
}

export async function prepareWebPushDevice(options = {}) {
    if (!supportsWebPush()) {
        return {
            status: 'unsupported',
            permission: null,
            subscription: null,
            config: null,
            isIOS: isIOSDevice(),
            isInstalled: isInstalledWebApp()
        };
    }

    let config;
    try {
        config = await loadWebPushConfig({force: options.force === true});
    } catch (error) {
        return {
            status: 'server-unavailable',
            permission: Notification.permission,
            subscription: null,
            config: null,
            error
        };
    }
    if (!config.enabled || !config.applicationServerKey) {
        return {
            status: 'server-unavailable',
            permission: Notification.permission,
            subscription: null,
            config
        };
    }

    const registration = await registerNotificationServiceWorker();
    if (!registration || !registration.pushManager) {
        return {
            status: 'unsupported',
            permission: Notification.permission,
            subscription: null,
            config
        };
    }

    preparedRegistration = registration;
    preparedConfig = config;
    preparedSubscription = await registration.pushManager.getSubscription();

    if (preparedSubscription) {
        if (options.syncExisting === true) {
            try {
                await saveSubscription(preparedSubscription);
            } catch (error) {
                return {
                    status: 'subscription-error',
                    permission: Notification.permission,
                    subscription: preparedSubscription,
                    config,
                    error
                };
            }
        }
        return {
            status: 'subscribed',
            permission: Notification.permission,
            subscription: preparedSubscription,
            config
        };
    }
    if (Notification.permission === 'denied') {
        return {
            status: 'denied',
            permission: 'denied',
            subscription: null,
            config
        };
    }
    if (Notification.permission === 'granted') {
        return {
            status: 'granted-unsubscribed',
            permission: 'granted',
            subscription: null,
            config
        };
    }
    return {
        status: 'default',
        permission: 'default',
        subscription: null,
        config
    };
}

export async function enableWebPushForCurrentDevice() {
    if (
        !preparedRegistration
        || !preparedRegistration.pushManager
        || !preparedConfig
        || !preparedConfig.applicationServerKey
    ) {
        const error = new Error('Web Push ещё не подготовлен');
        error.code = 'WEB_PUSH_NOT_PREPARED';
        throw error;
    }

    // subscribe() is deliberately called before the first await so WebKit sees
    // it as a direct continuation of the user's button click.
    const subscriptionPromise = preparedSubscription
        ? Promise.resolve(preparedSubscription)
        : preparedRegistration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: base64UrlToUint8Array(
                preparedConfig.applicationServerKey
            )
        });
    const subscription = await subscriptionPromise;
    preparedSubscription = subscription;
    await saveSubscription(subscription);
    return subscription;
}

export async function disableWebPushForCurrentDevice() {
    if (!preparedSubscription && preparedRegistration && preparedRegistration.pushManager) {
        preparedSubscription = await preparedRegistration.pushManager.getSubscription();
    }
    if (!preparedSubscription) {
        return;
    }

    const endpoint = preparedSubscription.endpoint;
    let serverError = null;
    try {
        await api.delete(WEB_PUSH_SUBSCRIPTIONS_URL, {
            data: {endpoint}
        });
    } catch (error) {
        serverError = error;
    }

    await preparedSubscription.unsubscribe();
    preparedSubscription = null;

    if (serverError) {
        throw serverError;
    }
}

export function shouldOfferNotificationOnboarding({
    isAuthorized,
    isPlatformRoute,
    deviceState,
    onboardingContext
}) {
    if (
        !isAuthorized
        || isPlatformRoute
        || isNotificationOnboardingSuppressed(onboardingContext)
    ) {
        return false;
    }
    return Boolean(
        deviceState
        && [
            'default',
            'granted-unsubscribed',
            'subscription-error'
        ].includes(deviceState.status)
    );
}
